import { showNotification } from '@mantine/notifications';
import { trackEvent } from '@repo/common/analytics';
import { Button, Icon } from '@repo/foundations';
import { useKeyPress } from 'ahooks';
import { observer } from 'mobx-react-lite';
import { useState } from 'react';
import { queryClient, useAuthUser } from '../../api';
import { useAutomationRun } from '../../api/hooks/automation';
import { AUTOMATION_SHORTCUTS } from '../../constants/shortcuts';

import { isNil } from 'lodash-es';
import { automationDailyAiOutputQueryKeyFactory } from '../../api/hooks/automation/constants.ts';
import { automationJobQueryKeyFactory } from '../../api/hooks/automationJob/constants.ts';
import { useBackgroundJob } from '../../api/hooks/backgroundJob/index.ts';
import type { BackgroundJob } from '../../lib/models/index.ts';
export interface RunAutomationProps {
	automationId: string;
	navigateToHistory: () => void;
	size?: 'sm' | 'md' | 'lg';
	disabled?: boolean;
}

function RunAutomationButton({
	automationId,
	size = 'md',
	disabled = false,
	navigateToHistory,
}: RunAutomationProps) {
	const { mutateAsync: runAutomation } = useAutomationRun();
	const [backgroundJob, setBackgroundJob] = useState<BackgroundJob | null>(
		null
	);
	const [loading, setLoading] = useState(false);
	const { user, workspace } = useAuthUser();

	const handleRunAutomation = async () => {
		try {
			trackEvent('automation/run_button/click', {}, user, workspace);
			const job = await runAutomation({
				data: {
					automationId,
				},
			});
			setBackgroundJob(job);
			setLoading(true);
			navigateToHistory();
			queryClient.invalidateQueries({
				queryKey: automationJobQueryKeyFactory.all(),
			});
			queryClient.invalidateQueries({
				queryKey: automationDailyAiOutputQueryKeyFactory.all(),
			});
		} catch (error) {
			showNotification({
				title: 'Could not run automation',
				message: 'Please contact customer support if the issue continues',
				color: 'red',
				icon: <Icon name="alertCircle" color="text/critical-on-fill/default" />,
			});
		}
	};

	useBackgroundJob({
		id: backgroundJob?.id || '',
		options: {
			enabled: !isNil(backgroundJob?.id),
			refetchInterval: (query) => {
				const { data } = query.state;

				if (data?.failed || data?.completed) {
					queryClient.invalidateQueries({
						queryKey: automationJobQueryKeyFactory.all(),
					});
					queryClient.invalidateQueries({
						queryKey: automationDailyAiOutputQueryKeyFactory.all(),
					});
					setLoading(false);
					return false;
				}

				return 1000;
			},
		},
	});
	useKeyPress(AUTOMATION_SHORTCUTS.RUN_CTRL, handleRunAutomation);
	useKeyPress(AUTOMATION_SHORTCUTS.RUN_META, handleRunAutomation);
	useKeyPress(AUTOMATION_SHORTCUTS.RUN_SHIFT, handleRunAutomation);

	return (
		<Button
			leftIconName={loading ? 'refresh' : undefined}
			leftIconAnimation="spinCounterClockwise"
			size={size}
			variant="primary"
			disabled={disabled}
			onClick={handleRunAutomation}
		>
			{loading ? 'Running' : 'Run automation'}
		</Button>
	);
}

const RunAutomationButtonObserver = observer(RunAutomationButton);
export default RunAutomationButtonObserver;
